import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Button from '@/components/atoms/Button/Button';
import Dialog from '@/components/atoms/Dialog/Dialog';
import Icon from '@/components/atoms/Icon/Icon';
import Pill from '@/components/atoms/Pill/Pill';
import { dataLayerPush } from '@/helpers/dataLayer';
import { announceStatus } from '@/utils/screenReader';
import clsx from 'clsx';
import { useState } from 'react';
import { usePreventScroll } from 'react-aria';
const isFilterApplied = (appliedFilters, filter, filterValue) => {
    return appliedFilters.some((appliedFilter) => appliedFilter.filterKey === filter.key && appliedFilter.key === filterValue.key);
};
const isAllApplied = (appliedFilters, filter) => {
    const appliedFiltersCount = appliedFilters.filter((appliedFilter) => appliedFilter.filterKey === filter.key).length;
    return appliedFiltersCount === 0;
};
const FilterDrawFilter = ({ filter, onToggle, onToggleAll, appliedFilters, allLabel, allAmount, }) => {
    return (_jsxs("div", { className: "grid gap-1", children: [_jsx("h3", { className: "type-heading-md", "data-testid": "filter-draw-label", children: filter.label }), _jsxs("ul", { className: "flex flex-wrap gap-0.5", role: "group", children: [_jsx("li", { children: _jsx(Pill, { variant: isAllApplied(appliedFilters, filter) ? 'solid' : 'outline', size: "lg", amount: allAmount, onToggle: () => onToggleAll(), testId: `filter-draw-pill-${filter.key}-all`, pressed: isAllApplied(appliedFilters, filter), children: allLabel }) }), filter.values.map((filterValue) => (_jsx("li", { children: _jsx(Pill, { variant: isFilterApplied(appliedFilters, filter, filterValue) ? 'solid' : 'outline', size: "lg", amount: filterValue.total, onToggle: () => onToggle(filterValue), testId: `filter-draw-pill-${filter.key}-${filterValue.key}`, pressed: isFilterApplied(appliedFilters, filter, filterValue), children: filterValue.label }) }, filterValue.key)))] })] }));
};
const FilterDraw = ({ filters, appliedFilters: initialAppliedFilters, onApply, onDismiss, modalLabel, closeLabel, closeIcon, clearAllLabel, clearedStatusLabel, applyLabel, allLabel, allAmount, parentComponent, }) => {
    usePreventScroll();
    const [clearAllPressedAndFiltersNotAdjusted, setClearAllPressedAndFiltersNotAdjusted] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters);
    const toggleFilter = (filter, filterValue) => {
        const isApplied = isFilterApplied(appliedFilters, filter, filterValue);
        setClearAllPressedAndFiltersNotAdjusted(false);
        setAppliedFilters((prevFilters) => {
            if (isApplied) {
                return prevFilters.filter((appliedFilter) => appliedFilter.filterKey !== filter.key || appliedFilter.key !== filterValue.key);
            }
            else {
                return [
                    ...prevFilters,
                    {
                        filterKey: filter.key,
                        label: filterValue.label,
                        key: filterValue.key,
                    },
                ];
            }
        });
    };
    const toggleAll = (filter) => {
        setAppliedFilters((prevFilters) => prevFilters.filter((appliedFilter) => appliedFilter.filterKey !== filter.key));
    };
    const appliedFilterKeyList = appliedFilters.map((filter) => filter.label).join(', ');
    const filtersDataLayerPush = (category) => {
        dataLayerPush('filterUsed', {
            filter_type: parentComponent,
            filter_category: category === 'Clear' ? undefined : appliedFilterKeyList,
            filter_interaction: category,
        });
    };
    const handleApply = () => {
        if (!clearAllPressedAndFiltersNotAdjusted) {
            filtersDataLayerPush('Apply');
        }
        onApply(appliedFilters);
    };
    const handleClose = () => {
        filtersDataLayerPush('Close');
        onDismiss();
    };
    const handleClearAll = () => {
        announceStatus(clearedStatusLabel);
        filtersDataLayerPush('Clear');
        setClearAllPressedAndFiltersNotAdjusted(true);
        setAppliedFilters([]);
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "fixed inset-0 transform-[opacity] bg-black/25 backdrop-blur-xs duration-500 starting:opacity-0", onClick: handleClose }), _jsxs(Dialog, { open: true, className: clsx('fixed flex transform-[transition] flex-col gap-0.5 bg-ui-specific-glass-white p-1.5 text-text-body duration-500', 'top-1/5 h-4/5 w-full starting:translate-y-full', 'md:top-0 md:h-full md:max-w-[450px] md:starting:-translate-x-full md:starting:translate-y-0'), ariaLabel: modalLabel, onDismiss: handleClose, theme: "light-a", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx(Button, { variant: "link", children: (props) => (_jsx("button", { ...props, onClick: handleClearAll, "data-testid": "filter-draw-close-btn", children: clearAllLabel })) }), _jsx("button", { className: "flex aspect-square w-2 cursor-pointer items-center justify-center bg-ui-specific-glass-dark md:w-3", onClick: handleClose, "aria-label": closeLabel, "data-testid": "filter-draw-close-btn", children: _jsx(Icon, { variant: closeIcon, className: "aspect-square w-1 md:w-1.5" }) })] }), _jsx("div", { className: "flex grow flex-col items-start gap-1.5 overflow-y-auto md:gap-2", children: filters.map((filter) => (_jsx(FilterDrawFilter, { filter: filter, appliedFilters: appliedFilters, onToggle: (filterValue) => toggleFilter(filter, filterValue), onToggleAll: () => toggleAll(filter), allLabel: allLabel, allAmount: allAmount }, filter.key))) }), _jsx(Button, { className: "w-full bg-surface-contrast text-text-contrast", children: (props) => (_jsx("button", { ...props, onClick: handleApply, "data-testid": "filter-draw-apply-btn", children: applyLabel })) })] })] }));
};
export default FilterDraw;
